import React, { useEffect, useState } from "react";
import { IComboBoxOption } from "@fluentui/react";
import { DimensionDetails, TgkDimensionAttributeSource } from "../interfaces/CoObject";
import { ObjectPanelTabs } from "./co-panel/ObjectPanelComponent";
import { fetchChildrenDimension, fetchDimensions, mapDimensionToDimensionItem } from "../../api/object/Dimensions";
import LoadingManager from "../../common/loading/LoadingManager";
import { DIMENSION_ATTRIBUTE_OBJECT_SOURCE } from "../../utils/constants/notification";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { Dimension, Dimension as ApiDimension } from "../../api/models/dimension";
import { TreeDropDownInput } from "./tree-pills-input/TreeInput";
import { Attribute } from "../../api/models/attribute";
import { AttributeSelect } from "./co-dimensionAttribute-panel/AttributeSelect";
import { DimensionSelect } from "./co-dimensionAttribute-panel/DimensionSelect";
import { DbApplicationDropDown } from "./DbApplicationDropdown";
import { fetchDocumentPartDetails } from "../../api/document/DocumentsPart";

interface CoObjectSourceDimensionAttributeProps {
  objectSource: TgkDimensionAttributeSource;
  updateCoObjectSourceDimensionAttribute: (obj: TgkDimensionAttributeSource, tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
  inheritableParameters: DimensionDetails[] | undefined;
  documentId: string;
}

export const CoObjectSourceDimensionAttributePanel: React.FunctionComponent<CoObjectSourceDimensionAttributeProps> = (
  props
) => {
  const {
    objectSource,
    updateCoObjectSourceDimensionAttribute,
    inheritableParameters,
    documentId,
    isComponentInstance,
    isLocked,
  } = props;

  const [{ selectedDimensionsItems, dimensionsRoot }, setSelectedDimensionsState] = useState<{
    selectedDimensionsItems: ApiDimension[];
    dimensionsRoot: ApiDimension[];
  }>({
    selectedDimensionsItems: [],
    dimensionsRoot: [],
  });

  useEffect(() => {
    if (objectSource?.dbApplication) {
      selectDBAndFetchDefaultRoots(
        { key: objectSource.dbApplication.id, text: objectSource.dbApplication.desc },
        documentId
      );
    } else {
      const objSource: TgkDimensionAttributeSource = {
        type: "DIMENSION_ATTRIBUTE",
        dbApplication: null,
        dimensions: [],
        locale: null,
      };
      updateCoObjectSourceDimensionAttribute(objSource, validateFields(objSource, 0), false);
    }

    return () => LoadingManager.remove(DIMENSION_ATTRIBUTE_OBJECT_SOURCE);
  }, []);

  function validateFields(obj: TgkDimensionAttributeSource, dimensionsCount: number): ObjectPanelTabs {
    const tab = { key: props.tab.key, name: props.tab.name, isValid: false };
    let hasAttribute = false;
    if (obj == null) return tab;
    const { dbApplication, dimensions } = obj;
    // @ts-ignore
    if (!dbApplication) return tab;
    if (dimensions?.length !== dimensionsCount) return tab;
    for (const d of dimensions ?? []) {
      if (!d?.dimensionOid || d.values.length !== 1) return tab;
      if (d.attributeCode) hasAttribute = true;
    }
    return { ...tab, isValid: hasAttribute };
  }

  async function selectDBAndFetchDefaultRoots(option: IComboBoxOption | undefined, documentId: string) {
    const modified = objectSource?.dbApplication?.id !== option?.key.toString();

    let obj: TgkDimensionAttributeSource = {
      type: "DIMENSION_ATTRIBUTE",
      dbApplication: option ? { id: String(option.key), desc: option.text } : null,
      dimensions: modified || !option ? [] : objectSource.dimensions,
      locale: null,
    };

    let selectedDimensionsItems: Dimension[] = [];
    let dimensionsRoot: Dimension[] = [];
    try {
      const newDocPart = await fetchDocumentPartDetails(documentId);
      obj.locale = newDocPart.metadata?.language;

      if (option) dimensionsRoot = (await fetchDimensions(option.key.toString())) ?? [];
      selectedDimensionsItems = (obj?.dimensions ?? [])
        .map((dimension) => {
          return dimensionsRoot.find((rootItem) => dimension.dimensionOid === rootItem.oid);
        })
        .filter((i) => i);
    } catch (e) {
      // eslint-disable-next-line no-undef
      console.error(e);
    }
    setSelectedDimensionsState({
      dimensionsRoot,
      selectedDimensionsItems,
    });
    updateCoObjectSourceDimensionAttribute(obj, validateFields(obj, selectedDimensionsItems.length), modified);
  }

  const onSelectDimension = (updateDimension) => {
    if (updateDimension.oid !== selectedDimensionsItems?.[0]?.oid) {
      setSelectedDimensionsState((s) => {
        return {
          ...s,
          selectedDimensionsItems: [updateDimension],
        };
      });
      const dimension = inheritableParameters?.filter((d) => d.dimensionOid === updateDimension.oid);

      const dimensionToAdd: DimensionDetails = {
        values: dimension?.[0]?.values ?? [],
        hierarchy: dimension?.[0]?.hierarchy,
        dimensionOid: updateDimension.oid,
        attributeCode: updateDimension?.attributeCode,
        inherited: !!dimension?.[0]?.values,
      };
      const newSourceDimensions = [dimensionToAdd];

      const obj: TgkDimensionAttributeSource = {
        dbApplication: objectSource.dbApplication,
        type: "DIMENSION_ATTRIBUTE",
        dimensions: newSourceDimensions,
        locale: objectSource.locale,
      };

      updateCoObjectSourceDimensionAttribute(obj, validateFields(obj, 1), true);
    }
  };

  const objSourceFirstDimension = objectSource?.dimensions?.find(
    (d) => d.dimensionOid === selectedDimensionsItems?.[0]?.oid
  );

  const onSelectAttribute = (attributeItem: Attribute) => {
    if (objSourceFirstDimension) {
      const isModified = objSourceFirstDimension.attributeCode !== attributeItem.code;

      const dependencies =
        attributeItem.dimensionsToEnable?.map((dep) => dimensionsRoot.find((dimension) => dimension.oid === dep)) ?? [];

      const objSourceDependencies: DimensionDetails[] = objectSource.dimensions.filter((oldDimension) =>
        dependencies.find((d) => oldDimension.dimensionOid === d.oid)
      );

      setSelectedDimensionsState((s) => {
        return {
          ...s,
          selectedDimensionsItems: [s.selectedDimensionsItems[0], ...dependencies],
        };
      });

      objSourceFirstDimension.attributeCode = attributeItem.code;
      const newDimensions = [objSourceFirstDimension, ...objSourceDependencies];

      const obj: TgkDimensionAttributeSource = {
        dbApplication: objectSource.dbApplication,
        type: "DIMENSION_ATTRIBUTE",
        dimensions: newDimensions,
        locale: objectSource.locale,
      };
      updateCoObjectSourceDimensionAttribute(obj, validateFields(obj, (dependencies?.length ?? 0) + 1), isModified);
    }
  };

  function updateFirstDimensionFromTree(dimensionItem: ApiDimension, newDimensions: DimensionDetails[]) {
    const oldDimension = objectSource.dimensions.find((d) => d.dimensionOid === dimensionItem.oid);
    const dimensionToAdd: DimensionDetails = {
      inherited: newDimensions?.[0]?.inherited,
      values: newDimensions?.[0]?.values ?? [],
      hierarchy: newDimensions?.[0]?.hierarchy,
      dimensionOid: dimensionItem.oid,
      attributeCode: oldDimension?.attributeCode,
    };
    const newSourceDimensions = [dimensionToAdd];
    newSourceDimensions.push(...objectSource.dimensions.filter((d) => d.dimensionOid !== dimensionItem.oid));

    const obj: TgkDimensionAttributeSource = {
      dbApplication: objectSource.dbApplication,
      type: "DIMENSION_ATTRIBUTE",
      dimensions: newSourceDimensions,
      locale: objectSource.locale,
    };

    updateCoObjectSourceDimensionAttribute(obj, validateFields(obj, selectedDimensionsItems.length), true);
  }

  function updateDependencyDimensionFromTree(dimensionItem: ApiDimension, newDimensions: DimensionDetails[]) {
    const dimensionToAdd = newDimensions?.map((newDimension) => ({ ...newDimension, dimensionOid: dimensionItem.oid }));

    const newSourceDimensions = objectSource.dimensions.filter((d) => d.dimensionOid !== dimensionItem.oid);
    newSourceDimensions.push(...dimensionToAdd);

    const obj: TgkDimensionAttributeSource = {
      dbApplication: objectSource.dbApplication,
      type: "DIMENSION_ATTRIBUTE",
      dimensions: newSourceDimensions,
      locale: objectSource.locale,
    };

    updateCoObjectSourceDimensionAttribute(obj, validateFields(obj, selectedDimensionsItems.length), true);
  }

  return (
    <>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <DbApplicationDropDown
              disabled={isComponentInstance || isLocked}
              dbApplication={objectSource?.dbApplication}
              selectDbComboBox={(option) => selectDBAndFetchDefaultRoots(option, documentId)}
            />
          </div>
          {objectSource?.dbApplication?.id && (
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
              <DimensionSelect
                disabled={isComponentInstance || isLocked}
                availableDimensions={dimensionsRoot}
                selectedKey={selectedDimensionsItems[0]?.oid}
                onChange={onSelectDimension}
              />
            </div>
          )}

          {selectedDimensionsItems
            ?.filter((i) => i)
            ?.map((dimensionItem, index) => (
              <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" key={dimensionItem.oid + "DropDown"}>
                <TreeDropDownInput
                  fetchRoot={async (oid) => {
                    const items = await fetchChildrenDimension(objectSource?.dbApplication?.id, oid);
                    return items.map((i) => mapDimensionToDimensionItem(i));
                  }}
                  fetchChildren={async (item) => {
                    const items = await fetchChildrenDimension(objectSource?.dbApplication?.id, item.data.oid);
                    return items.map((i) => mapDimensionToDimensionItem(i));
                  }}
                  dimensions={objectSource?.dimensions.filter(
                    (d) => d.dimensionOid === dimensionItem.oid && d.values.length
                  )}
                  label={dimensionItem.desc + " *"}
                  modalTitle={dimensionItem.desc}
                  placeholder={formatMessage("cof.base.select")}
                  rootOid={dimensionItem.oid}
                  hierarchical={dimensionItem.hierarchical}
                  canInherit={inheritableParameters?.filter((d) => d.dimensionOid === dimensionItem.oid) ?? []}
                  inherited={
                    objectSource?.dimensions?.filter((d) => d.dimensionOid === dimensionItem.oid)?.[index]?.inherited
                  }
                  multiSelectable={dimensionItem.multiSelectable}
                  onChange={(newDimension) => {
                    (index ? updateDependencyDimensionFromTree : updateFirstDimensionFromTree)(
                      dimensionItem,
                      newDimension
                    );
                  }}
                  dbId={objectSource?.dbApplication?.id}
                  disabled={isComponentInstance || isLocked}
                  isSearchAvailable={true}
                />
              </div>
            ))}

          {selectedDimensionsItems[0] && (
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
              <AttributeSelect
                dbId={objectSource.dbApplication.id}
                dimensionOid={selectedDimensionsItems[0].oid}
                selectedKey={objSourceFirstDimension?.attributeCode}
                onChange={onSelectAttribute}
                disabled={isComponentInstance || isLocked}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
